// vi: filetype=javascript.jsx
import { T } from "@classdojo/web";
import { BodyText, Button, DisplayText } from "@classdojo/web/nessie";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import React from "react";
import { compose } from "recompose";
import ConundrumCard from "./ConundrumCard";
import Tag from "./Tag";
import { Title as introTitle, Description as introDesc } from "./firstActivity/definition";
import {
  Title as mojoLisaTitle,
  Description as mojoLisaDesc,
  QuestionText as mojoLisaQuestion,
} from "./mojoLisa/definition";
import { BackButton } from "src/shared/components/BackButton";
import withRouteParams from "src/shared/hoc/withRouteParams";
import logEvent from "src/utils/logEvent";
import { navigate } from "src/utils/reactRouterNavigate";
import civicServantsImage from "src/pods/conundrums/assets/civicServantsListImage@2x.jpg";
import dojosaurusList from "src/pods/conundrums/assets/dojosaurusList.png";
import introConundrumImage from "src/pods/conundrums/assets/intro@2x.jpg";
import mojoLisaImage from "src/pods/conundrums/assets/mojoLisaList.png";
import spaceStationListImage from "src/pods/conundrums/assets/spaceStationList.png";

type OuterListProps = {
  params: {
    mountPoint?: string;
    classId?: string;
  };
};
type ListProps = {
  mountPoint: string;
  classId: string;
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
class List extends React.Component<ListProps> {
  componentDidMount() {
    logEvent({
      eventName: `conundrums.list`,
    });
  }

  render() {
    const { classId, mountPoint } = this.props;
    return (
      <div sx={appStyle}>
        <BackButton
          cssStyle={{ padding: RAW_cssValue(20) }}
          onClick={() => navigate(`/apps/remote/${mountPoint}/${classId}`)}
        />
        <img
          src={"https://static.classdojo.com/img/ideas_platform/activities/conundrums/conundrums@2x.png"}
          alt="Conundrum with Ad Astra"
          sx={{ height: "180px" }}
        />
        <DisplayText as="h1" level={4} sx={titleStyle}>
          <T str="dojo.toolkit:list.conundrums" fallback="Conundrums" />
        </DisplayText>
        <BodyText sx={descriptionStyle}>
          <T
            str="dojo.toolkit:list.quick_fun_inquiry"
            fallback="Quick, fun, inquiry-based questions to get students thinking critically, discussing positively, and supporting their opinions! Created by Josh Dahn, head of school at Ad Astra."
          />
        </BodyText>
        <div sx={{ margin: RAW_cssValue("18 18 0") }}>
          <Tag color="dt_content_tertiary" size="small">
            <T str="dojo.toolkit:list.inquiry_based_learning" fallback="Inquiry-based learning" />
          </Tag>
          <Tag color="dt_content_tertiary" size="small">
            <T str="dojo.toolkit:list.communication" fallback="Communication" />
          </Tag>
          <Tag color="dt_content_tertiary" size="small">
            <T str="dojo.toolkit:list.critical_thinking" fallback="Critical Thinking" />
          </Tag>
          <Tag color="dt_content_tertiary" size="small">
            <T str="dojo.toolkit:list.discussion" fallback="Discussion" />
          </Tag>
        </div>
        <div sx={cardsWrapper}>
          <ConundrumCard
            mojo={introConundrumImage}
            href={`/apps/remote/${mountPoint}/${classId}/conundrums/firstActivity/0`}
            title={introTitle}
            description={introDesc}
            duration="5-10"
          />
          <ConundrumCard
            mojo={civicServantsImage}
            href={`/apps/remote/${mountPoint}/${classId}/conundrums/civicServants/0`}
            title="Civic Servants"
            description={
              <span>
                <T
                  htmlStr="dojo.toolkit:list.mojo_is_the"
                  fallback="Mojo is the mayor of Monster Island, and must decide: <b>who should make the most money?</b>"
                />
              </span>
            }
            duration="10-20"
          />

          <ConundrumCard
            mojo={mojoLisaImage}
            href={`/apps/remote/${mountPoint}/${classId}/conundrums/mojoLisa/0`}
            title={mojoLisaTitle}
            description={
              <span>
                {mojoLisaDesc} <b>{mojoLisaQuestion}</b>
              </span>
            }
            duration="10-20"
          />
          <ConundrumCard
            mojo={dojosaurusList}
            href={`/apps/remote/${mountPoint}/${classId}/conundrums/dojosaurus/0`}
            title="Dojosaurus"
            description={
              <span>
                <T
                  str="dojo.toolkit:list.mojo_and_his"
                  fallback="Mojo and his friends have discovered a massive dinosaur on someone else's land!"
                />{" "}
                <b>
                  <T str="dojo.toolkit:list.who_does_it" fallback="Who does it belong to?" />
                </b>
              </span>
            }
            duration="10-20"
          />
          <ConundrumCard
            mojo={spaceStationListImage}
            href={`/apps/remote/${mountPoint}/${classId}/conundrums/spaceStation/0`}
            title="Space Station"
            description={
              <span>
                <T
                  htmlStr="dojo.toolkit:list.mojo_and_friends"
                  fallback="Mojo and friends are creating a space station, but they can't decide: <b>Who should be in charge?</b>"
                />
              </span>
            }
            duration="10-20"
          />
        </div>
        <div sx={feedbackStyle}>
          <BodyText sx={{ marginTop: "dt_l", marginBottom: "dt_s" }}>
            <T str="dojo.toolkit:list.more_conundrums_are" fallback="More Conundrums are coming soon!" />
          </BodyText>
          <BodyText>
            <T str="dojo.toolkit:list.have_an_idea" fallback="Have an idea?" />
          </BodyText>
          <Button
            data-name="list:render:list.let_us_know"
            sx={{ margin: "auto", marginTop: "xs" }}
            kind="tertiary"
            onClick={this.goToFeedback}
          >
            <T str="dojo.toolkit:list.let_us_know" fallback="Let us know!" />
          </Button>
        </div>
      </div>
    );
  }

  goToFeedback = () => {
    const { classId, mountPoint } = this.props;

    navigate(`/apps/remote/${mountPoint}/${classId}/conundrums/feedback`);
  };
}

export default compose<ListProps, OuterListProps>(withRouteParams(["classId", "mountPoint"]))(List);

const appStyle: ThemeUIStyleObject = {
  textAlign: "center",
  padding: RAW_cssValue("20px 8px"),
};

const titleStyle: ThemeUIStyleObject = {
  textAlign: "center",
  margin: "dt_s",
};

const cardsWrapper: ThemeUIStyleObject = {
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  flexFlow: "row wrap",
  justifyContent: "space-evenly",
};

const descriptionStyle: ThemeUIStyleObject = {
  textAlign: "center",
  marginBottom: "dt_m",
};

const feedbackStyle: ThemeUIStyleObject = {
  padding: "dt_xl",
  fontSize: "16px",
};
